import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useU2000AxiosAuthenticated from '../u2000Core/axios/U2000UseAxiosAuthenticated';
import { Outlet } from 'react-router-dom';
import i18next from 'i18next';
import { useU2000InitLUHandlers } from '../u2000Core/hooks/handler/U2000UseInitLUHandlers';
import { U2000HandleApiError, type U2000ApiErrorResult } from '../u2000Core/axios/U2000HandleApiError';
import { useToast } from '../u2000Core/context/toast/U2000ToastContext';
import { doInitLU, luHandleAppiError, type U3203InitLU } from './U3203Service';
import { U3203ContextProvider } from './U3203Context';
import { useU2000LogicalUnitContext } from '../u2000Core/context/logicalUnit/U2000LogicalUnitContext';

export const NS = 'u3203';

export const U3203ROLES = {
  READ: 'U3203:READ',
  ADD: 'U3203:ADD',
  EDIT: 'U3203:EDIT',
  DELETE: 'U3203:DELETE',
  AUDITS: 'U3203:AUDITS',
} as const;
export type U3203ROLES = (typeof U3203ROLES)[keyof typeof U3203ROLES];

const defaultInitLU: U3203InitLU = {
  launchSearch: false,
  hideSearch: true,
  nbRecordPerPage: 50,
  logicalUnitCode: 'U3203',
  categories: ['extraction', 'assistant', 'analyse'],
  templateVariableDefinitions: [],
};

const U3203: React.FC = () => {
  const { i18n } = useTranslation();
  const [i18nReady, setI18nReady] = useState<boolean>(false);
  const { t } = useTranslation();
  const toast = useToast();
  const [initLUReady, setInitLUReady] = useState<boolean>(false);
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();
  const [initLUData, setInitLUData] = useState<U3203InitLU>(defaultInitLU);

  useEffect(() => {
    i18next.loadNamespaces(['u3203', 'u3202'], (err) => {
      if (err) return console.error('Error loading namespace:', err);
      setI18nReady(true);
    });
  }, [i18n]);

  const handleInitLU = useU2000InitLUHandlers<U3203InitLU>({
    doInitLUFn: async () => {
      return await doInitLU(u2000AxiosAuthenticated);
    },
    onSuccess: (initLU: U3203InitLU) => {
      setInitLUData({
        ...defaultInitLU,
        ...initLU,
        logicalUnitCode: initLU.logicalUnitCode ?? 'U3203',
        categories: initLU.categories ?? defaultInitLU.categories,
        templateVariableDefinitions: initLU.templateVariableDefinitions ?? [],
      });
      setInitLUReady(true);
    },
    onError: ({ error }: { error: unknown }) => {
      const errorResult = U2000HandleApiError(error as any, t, NS, luHandleAppiError) as U2000ApiErrorResult;
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
      setInitLUReady(true);
    },
  });

  useEffect(() => {
    handleInitLU.getInitLU();
  }, [t]); // eslint-disable-line react-hooks/exhaustive-deps

  const { setLogicalUnitTitle, setLogicalUnitBreadcrumb } = useU2000LogicalUnitContext();
  useEffect(() => {
    if (!i18nReady || !initLUReady) return;
    setLogicalUnitTitle(t('title', { ns: NS }));

    return () => {
      setLogicalUnitTitle(null);
      setLogicalUnitBreadcrumb([]);
    };
  }, [i18nReady, initLUReady, setLogicalUnitTitle, setLogicalUnitBreadcrumb, t]);

  if (!i18nReady || !initLUReady) {
    return null;
  }

  return (
    <U3203ContextProvider initLU={initLUData}>
      <Outlet />
    </U3203ContextProvider>
  );
};

export default U3203;
